import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    IconButton,
    Typography,
    Grid,
    CircularProgress,
    Tooltip,
    Select,
    MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import moment from 'moment';
import * as API from 'services/api';
import { LEAD_TYPES, LEAD_STATUSES, LEAD_EXTERNAL_STATUSES } from 'config';
import { showMessage } from 'utils';
import useHomeContext from 'layouts/HomeLayout/context';

const useStyles = makeStyles(() => ({
    backdrop: {
        backgroundColor: 'rgba(6, 56, 63, 0.45) !important',
    },
    modalRoot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4vh 2vw',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '92vh',
    },
    paper: {
        position: 'relative',
        width: 'min(1420px, 96vw)',
        maxHeight: '92vh',
        minHeight: '92vh',
        borderRadius: 10,
        backgroundColor: '#f1f3f9',
        boxShadow: '0px 4px 35px 0px rgba(6, 56, 63, 0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
    },
    header: {
        backgroundColor: '#030f40',
        minHeight: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 25px',
        flexShrink: 0,
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: '0.9px',
        color: '#fff',
        lineHeight: '25px',
    },
    closeBtn: {
        color: '#fff',
        padding: 4,
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5) !important',
        },
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexShrink: 0,
    },
    headerBtn: {
        color: '#fff',
        padding: 4,
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5) !important',
        },
    },
    body: {
        overflowY: 'auto',
        padding: '32px 37px 40px',
        flex: 1,
    },
    tabsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 41,
    },
    tabActive: {
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: '0.7px',
        textTransform: 'uppercase',
        color: '#fff',
        backgroundColor: '#030f40',
        border: '1.5px solid #aab1cf',
        borderRadius: 35,
        height: 45,
        minWidth: 257,
        padding: '0 28px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default',
        boxSizing: 'border-box',
    },
    tabInactive: {
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: '0.7px',
        textTransform: 'uppercase',
        color: '#030f40',
        backgroundColor: '#f1f3f9',
        border: '1.5px solid #aab1cf',
        borderRadius: 35,
        height: 45,
        minWidth: 257,
        padding: '0 28px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    tabButton: {
        outline: 'none',
        font: 'inherit',
        fontSize: 14,
        margin: 0,
    },
    dataTableHeader: {
        display: 'grid',
        gridTemplateColumns: '180px 1fr 220px',
        gap: 12,
        padding: '0 34px 14px',
    },
    dataTableHeaderHistory: {
        display: 'grid',
        gridTemplateColumns: '140px 160px 160px 160px 1fr',
        gap: 12,
        padding: '0 34px 14px',
    },
    dataTableHeaderCell: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color: '#6e7697',
        lineHeight: '16px',
    },
    dataTableWrap: {
        borderRadius: 16,
        backgroundColor: '#fff',
        overflow: 'hidden',
    },
    dataTableRow: {
        display: 'grid',
        gridTemplateColumns: '180px 1fr 220px',
        gap: 12,
        padding: '14px 34px',
        minHeight: 44,
        alignItems: 'center',
    },
    dataTableRowHistory: {
        display: 'grid',
        gridTemplateColumns: '140px 160px 160px 160px 1fr',
        gap: 12,
        padding: '14px 34px',
        minHeight: 44,
        alignItems: 'center',
    },
    dataTableRowAlt: {
        backgroundColor: '#f8f9fd',
    },
    dataTableCell: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: '0.6px',
        color: '#0e1927',
        lineHeight: '16px',
        wordBreak: 'break-word',
    },
    emptyTabMessage: {
        fontSize: 14,
        color: '#6e7697',
        padding: '24px 0',
    },
    sectionBlock: {
        marginTop: 41,
    },
    sectionBlockFirst: {
        marginTop: 0,
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: '0.9px',
        color: '#030f40',
        marginBottom: 21,
        lineHeight: '25px',
    },
    fieldWrap: {
        marginBottom: 0,
    },
    fieldWrapStretch: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        marginBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: 12,
        color: '#061024',
        marginBottom: 7,
        lineHeight: '19px',
        display: 'block',
    },
    fieldBox: {
        backgroundColor: '#fff',
        border: '1px solid #e5e3f2',
        borderRadius: 5,
        height: 40,
        padding: '0 18px',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
    },
    fieldBoxSelect: {
        padding: 0,
    },
    fieldBoxMultiline: {
        alignItems: 'flex-start',
        height: 'auto',
        minHeight: 195,
        padding: '12px 18px',
    },
    fieldBoxStretch: {
        flex: 1,
        height: 'auto',
        minHeight: 0,
        alignItems: 'flex-start',
        padding: '12px 18px',
    },
    fieldValue: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        color: '#030f40',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        flex: 1,
        paddingTop: 0,
    },
    selectArrow: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#030f40',
        fontSize: 22,
        pointerEvents: 'none',
    },
    fieldSelect: {
        flex: 1,
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        fontSize: 12,
        fontWeight: 400,
        color: '#030f40',
        backgroundColor: 'transparent',
        '&.MuiInputBase-root': {
            padding: 0,
        },
        '&:focus': {
            backgroundColor: 'transparent',
        },
        '&:before, &:after': {
            display: 'none',
        },
    },
    fieldSelectInner: {
        paddingRight: '36px !important',
        paddingLeft: '18px !important',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        minHeight: 40,
        boxSizing: 'border-box',
    },
    selectMenuPaper: {
        borderRadius: 5,
        border: '1px solid #e5e3f2',
        boxShadow: '0px 4px 12px rgba(6, 56, 63, 0.12)',
        marginTop: 2,
        maxHeight: 280,
        boxSizing: 'border-box',
    },
    selectMenuItem: {
        fontSize: 12,
        fontWeight: 400,
        textTransform: 'uppercase',
        color: '#030f40',
        minHeight: 36,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        '&.Mui-selected': {
            backgroundColor: '#030f40 !important',
            color: '#fff',
        },
        '&.Mui-selected:hover': {
            backgroundColor: '#030f40 !important',
        },
        '&:hover': {
            backgroundColor: '#f1f3f9',
        },
    },
    fieldInput: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        color: '#030f40',
        border: 'none',
        background: 'transparent',
        width: '100%',
        height: '100%',
        outline: 'none',
        padding: 0,
        resize: 'none',
        boxSizing: 'border-box',
    },
    dualColumnsRow: {
        display: 'flex',
        gap: 70,
        alignItems: 'flex-start',
        width: '100%',
    },
    dualColumn: {
        flex: '1 1 0',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    fullWidthColumn: {
        flex: '1 1 100%',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
    },
    rowGap: {
        marginBottom: 20,
    },
    orderDetailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 73fr) minmax(0, 27fr)',
        columnGap: 20,
        rowGap: 20,
        alignItems: 'stretch',
    },
    orderCommentCell: {
        gridColumn: 2,
        gridRow: '1 / 4',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    orderDetailsLeftRow: {
        gridColumn: 1,
    },
    leadManagementRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.12fr) minmax(0, 1fr)',
        columnGap: 20,
        alignItems: 'start',
        width: '100%',
    },
    leadManagementLeft: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 24,
        minWidth: 0,
        width: '100%',
    },
    leadManagementContratCadreCell: {
        gridColumn: '1',
    },
    leadManagementOffresCell: {
        gridColumn: '2',
    },
    leadManagementCommentCol: {
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
    },
    traineesSection: {
        marginTop: 41,
    },
    traineesHeader: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 1.2fr 1.4fr 1.6fr 1.2fr',
        gap: 12,
        padding: '0 34px 14px',
    },
    traineesHeaderCell: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color: '#6e7697',
        lineHeight: '16px',
    },
    traineesWrap: {
        borderRadius: 16,
        backgroundColor: '#fff',
        overflow: 'hidden',
    },
    traineeRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 1.2fr 1.4fr 1.6fr 1.2fr',
        gap: 12,
        padding: '14px 34px',
        minHeight: 44,
        alignItems: 'center',
    },
    traineeRowAlt: {
        backgroundColor: '#f8f9fd',
    },
    traineeCell: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: '0.6px',
        color: '#0e1927',
        textTransform: 'uppercase',
        lineHeight: '16px',
        wordBreak: 'break-word',
    },
    traineeCellEmail: {
        fontWeight: 400,
        fontSize: 12,
        color: '#0e1927',
        lineHeight: '16px',
        wordBreak: 'break-word',
        textTransform: 'none',
        letterSpacing: 0,
    },
    loadingWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: 48,
    },
}));

const TABS = ['general', 'events', 'history', 'client'];
const LEAD_REFERENCES = ['bb', 'pk', 'cv'];
const TAB_I18N = {
    general: 'leads.detail.tab.general',
    events: 'leads.detail.tab.events',
    history: 'leads.detail.tab.history',
    client: 'leads.detail.tab.client',
};

const resolveClientId = (record, company, mainCompany) => {
    const candidate = record?.clientId || company?.id || mainCompany?.id;
    if (!candidate || candidate === '0000') {
        return null;
    }
    return candidate;
};

const resolveClientSiret = (company, mainCompany, signator, packData) => (
    company?.siret
    || mainCompany?.siret
    || signator?.siret
    || packData?.company?.siret
    || ''
);

const formatStatusLabel = (formatMessage, value) => {
    if (!value) {
        return '';
    }
    try {
        return formatMessage({ id: `leads.status.${value}` }).toUpperCase();
    } catch (e) {
        return String(value).toUpperCase();
    }
};

const formatReferenceLabel = (formatMessage, value) => {
    if (!value) {
        return '';
    }
    try {
        return formatMessage({ id: `leads.table.reference.${value}` }).toUpperCase();
    } catch (e) {
        return String(value).toUpperCase();
    }
};

const formatLeadEventDescription = (formatMessage, event) => {
    const user = event.userName || 'System';
    switch (event.type) {
        case 'creation':
            return formatMessage({ id: 'leads.detail.events.creation' });
        case 'status_internal':
            return formatMessage(
                { id: 'leads.detail.events.statusInternal' },
                {
                    from: formatStatusLabel(formatMessage, event.fromValue) || '-',
                    to: formatStatusLabel(formatMessage, event.toValue) || '-',
                },
            );
        case 'status_external':
            return formatMessage(
                { id: 'leads.detail.events.statusExternal' },
                {
                    from: formatStatusLabel(formatMessage, event.fromValue) || '-',
                    to: formatStatusLabel(formatMessage, event.toValue) || '-',
                },
            );
        case 'reference':
            return formatMessage(
                { id: 'leads.detail.events.reference' },
                {
                    from: formatReferenceLabel(formatMessage, event.fromValue) || '-',
                    to: formatReferenceLabel(formatMessage, event.toValue) || '-',
                },
            );
        case 'comment':
            return formatMessage({ id: 'leads.detail.events.comment' }, { user });
        case 'offres_oc_plus':
            return formatMessage({ id: 'leads.detail.events.offresOcPlus' }, { user });
        default:
            return event.type || '';
    }
};

const getLeadProductLabel = (formatMessage, item) => {
    if (!item) {
        return '';
    }
    const { source = {}, type } = item;
    if (type === LEAD_TYPES[1]) {
        return item.apave ? `Question Client ${item.apave.title}` : (source.description || '');
    }
    if (type === LEAD_TYPES[2] || type === LEAD_TYPES[3]) {
        if (source.productType === 'pack') {
            return formatMessage({ id: 'home.catalogue.field.catalogueType.pack' });
        }
        return source.title ? `Commande ${source.title}` : '';
    }
    return source.title || source.description || '';
};

const getSiren = (siret = '') => {
    const digits = String(siret).replace(/\D/g, '');
    return digits.length >= 9 ? digits.slice(0, 9) : '';
};

const parsePackData = (source) => {
    if (!source?.data) return null;
    try {
        return typeof source.data === 'string' ? JSON.parse(source.data) : source.data;
    } catch (e) {
        return null;
    }
};

const ReadOnlyField = ({
    classes,
    label,
    value,
    multiline,
    selectStyle,
    stretch,
}) => (
    <div className={stretch ? classes.fieldWrapStretch : classes.fieldWrap}>
        <Typography className={classes.fieldLabel} component="label">{label}</Typography>
        <div className={[
            classes.fieldBox,
            multiline && !stretch ? classes.fieldBoxMultiline : '',
            stretch ? classes.fieldBoxStretch : '',
        ].filter(Boolean).join(' ')}>
            <Typography className={classes.fieldValue} component="span">{value || ''}</Typography>
            {selectStyle && <KeyboardArrowDownIcon className={classes.selectArrow} />}
        </div>
    </div>
);

ReadOnlyField.propTypes = {
    classes: PropTypes.object.isRequired,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    multiline: PropTypes.bool,
    selectStyle: PropTypes.bool,
    stretch: PropTypes.bool,
};

const EditableSelectField = ({
    classes,
    label,
    value,
    onChange,
    options,
    displayEmpty,
}) => {
    const anchorRef = useRef(null);
    const [menuWidth, setMenuWidth] = useState(0);

    const handleOpen = () => {
        if (anchorRef.current) {
            setMenuWidth(anchorRef.current.getBoundingClientRect().width);
        }
    };

    return (
        <div className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel} component="label">{label}</Typography>
            <div ref={anchorRef} className={`${classes.fieldBox} ${classes.fieldBoxSelect}`}>
                <Select
                    value={value}
                    onChange={onChange}
                    onOpen={handleOpen}
                    disableUnderline
                    displayEmpty={displayEmpty}
                    className={classes.fieldSelect}
                    classes={{ select: classes.fieldSelectInner }}
                    IconComponent={() => null}
                    MenuProps={{
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        getContentAnchorEl: null,
                        marginThreshold: 0,
                        classes: { paper: classes.selectMenuPaper },
                        PaperProps: {
                            style: {
                                width: menuWidth || undefined,
                                maxHeight: 280,
                            },
                        },
                    }}
                >
                    {options.map((option) => (
                        <MenuItem
                            key={option.value || '__empty'}
                            value={option.value}
                            className={classes.selectMenuItem}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                <KeyboardArrowDownIcon className={classes.selectArrow} />
            </div>
        </div>
    );
};

EditableSelectField.propTypes = {
    classes: PropTypes.object.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
    })).isRequired,
    displayEmpty: PropTypes.bool,
};

const EditableTextField = ({
    classes,
    label,
    value,
    onChange,
    multiline,
    stretch,
}) => (
    <div className={stretch ? classes.fieldWrapStretch : classes.fieldWrap}>
        <Typography className={classes.fieldLabel} component="label">{label}</Typography>
        <div className={[
            classes.fieldBox,
            multiline && !stretch ? classes.fieldBoxMultiline : '',
            stretch ? classes.fieldBoxStretch : '',
        ].filter(Boolean).join(' ')}>
            {multiline ? (
                <textarea
                    className={classes.fieldInput}
                    value={value}
                    onChange={onChange}
                    rows={stretch ? 3 : 8}
                />
            ) : (
                <input
                    type="text"
                    className={classes.fieldInput}
                    value={value}
                    onChange={onChange}
                />
            )}
        </div>
    </div>
);

EditableTextField.propTypes = {
    classes: PropTypes.object.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    multiline: PropTypes.bool,
    stretch: PropTypes.bool,
};

const fieldLabel = (formatMessage, id) => formatMessage({ id: `leads.detail.label.${id}` });

const CompanyBlock = ({ classes, formatMessage, company, title, fullWidth }) => {
    if (!company) {
        return null;
    }

    return (
        <div className={fullWidth ? classes.fullWidthColumn : classes.dualColumn}>
            <Typography className={classes.sectionTitle}>{title}</Typography>
            <Grid container spacing={0}>
                <Grid item xs={12} className={classes.rowGap}>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <ReadOnlyField
                                classes={classes}
                                label={fieldLabel(formatMessage, 'siret')}
                                value={company.siret}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ReadOnlyField
                                classes={classes}
                                label={fieldLabel(formatMessage, 'siren')}
                                value={getSiren(company.siret)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className={classes.rowGap}>
                    <ReadOnlyField
                        classes={classes}
                        label={fieldLabel(formatMessage, 'company')}
                        value={company.company || company.name}
                    />
                </Grid>
                <Grid item xs={12} className={classes.rowGap}>
                    <ReadOnlyField
                        classes={classes}
                        label={fieldLabel(formatMessage, 'address')}
                        value={company.address}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={3}>
                        <Grid item xs={4}>
                            <ReadOnlyField
                                classes={classes}
                                label={fieldLabel(formatMessage, 'zipcode')}
                                value={company.zipcode || company.zipCode}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <ReadOnlyField
                                classes={classes}
                                label={fieldLabel(formatMessage, 'city')}
                                value={company.city}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <ReadOnlyField
                                classes={classes}
                                label={fieldLabel(formatMessage, 'country')}
                                value={company.country || company.pays}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};

CompanyBlock.propTypes = {
    classes: PropTypes.object.isRequired,
    formatMessage: PropTypes.func.isRequired,
    company: PropTypes.object,
    title: PropTypes.string.isRequired,
    fullWidth: PropTypes.bool,
};

const ContactBlock = ({ classes, formatMessage, contact, title }) => (
    <div className={classes.dualColumn}>
        <Typography className={classes.sectionTitle}>{title}</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
                <ReadOnlyField
                    classes={classes}
                    label={fieldLabel(formatMessage, 'lastname')}
                    value={contact?.lastname || contact?.lastName}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <ReadOnlyField
                    classes={classes}
                    label={fieldLabel(formatMessage, 'firstname')}
                    value={contact?.firstname || contact?.firstName}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <ReadOnlyField
                    classes={classes}
                    label={fieldLabel(formatMessage, 'email')}
                    value={contact?.email}
                />
            </Grid>
        </Grid>
    </div>
);

ContactBlock.propTypes = {
    classes: PropTypes.object.isRequired,
    formatMessage: PropTypes.func.isRequired,
    contact: PropTypes.object,
    title: PropTypes.string.isRequired,
};

const FormDetailsModern = ({ formatMessage, data, open, handleClose, companies, onSaved, onDeleted }) => {
    const classes = useStyles();
    const { onDeleteById } = useHomeContext();
    const [activeTab, setActiveTab] = useState(TABS[0]);
    const [loading, setLoading] = useState(false);
    const [tabLoading, setTabLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lead, setLead] = useState(null);
    const [clientHistory, setClientHistory] = useState([]);
    const [clientDetails, setClientDetails] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [mainCompany, setMainCompany] = useState(null);
    const [status, setStatus] = useState('');
    const [statusExternal, setStatusExternal] = useState('');
    const [reference, setReference] = useState('');
    const [managementComment, setManagementComment] = useState('');
    const [offresOcPlus, setOffresOcPlus] = useState('');

    useEffect(() => {
        if (!open) {
            setActiveTab(TABS[0]);
            setClientHistory([]);
            setClientDetails(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !data?.id) return;

        setLoading(true);
        API.getLead(data.id)
            .then((res) => {
                if (res.code === 200) {
                    setLead(res.data);
                    setRecipients(res.data.source?.recipients || []);
                    setMainCompany(res.data.mainCompany || null);
                    setStatus(res.data.status || LEAD_STATUSES[0]);
                    setStatusExternal(res.data.statusExternal || LEAD_EXTERNAL_STATUSES[0]);
                    setReference(res.data.reference || '');
                    setManagementComment(res.data.managementComment || '');
                    setOffresOcPlus(res.data.offresOcPlus || res.data.source?.offresOcPlus || '');
                } else {
                    setLead(data);
                    setStatus(data.status || LEAD_STATUSES[0]);
                    setStatusExternal(data.statusExternal || LEAD_EXTERNAL_STATUSES[0]);
                    setReference(data.reference || '');
                    setManagementComment(data.managementComment || '');
                    setOffresOcPlus(data?.offresOcPlus || data?.source?.offresOcPlus || '');
                }
            })
            .catch(() => {
                setLead(data);
                setStatus(data?.status || LEAD_STATUSES[0]);
                setStatusExternal(data?.statusExternal || LEAD_EXTERNAL_STATUSES[0]);
                setReference(data?.reference || '');
                setManagementComment(data?.managementComment || '');
                setOffresOcPlus(data?.offresOcPlus || data?.source?.offresOcPlus || '');
            })
            .finally(() => setLoading(false));
    }, [open, data?.id]);

    const record = lead || data;
    const { source = {}, recipientInfo, userId, clientId, createdAt, id } = record || {};
    const resolvedContratCadre = record?.contratCadre || record?.clientContractReference || '';
    const packData = parsePackData(source);
    const company = companies.find((el) => el.id === clientId);
    const companyToShow = company || mainCompany;
    const signator = recipientInfo?.signatorInfo || {};
    const resolvedClientId = resolveClientId(record, company, mainCompany);
    const resolvedClientSiret = resolveClientSiret(companyToShow, mainCompany, signator, packData);

    useEffect(() => {
        if (!open || loading || activeTab !== 'history') {
            return;
        }

        setTabLoading(true);
        const params = { page: 1, perPage: 200 };
        if (resolvedClientSiret) {
            params.siret = resolvedClientSiret;
        } else if (resolvedClientId) {
            params.clientId = resolvedClientId;
        } else {
            setClientHistory([]);
            setTabLoading(false);
            return;
        }

        API.listLeads(params)
            .then((res) => {
                if (res.code === 200) {
                    setClientHistory(res.data || []);
                } else {
                    setClientHistory([]);
                }
            })
            .catch(() => setClientHistory([]))
            .finally(() => setTabLoading(false));
    }, [open, loading, activeTab, resolvedClientId, resolvedClientSiret]);

    useEffect(() => {
        if (!open || loading || activeTab !== 'client') {
            return;
        }

        if (!resolvedClientId) {
            setClientDetails(mainCompany || companyToShow || null);
            return;
        }

        setTabLoading(true);
        API.getClient(resolvedClientId)
            .then((res) => {
                if (res.code === 200) {
                    setClientDetails(res.data);
                } else {
                    setClientDetails(mainCompany || companyToShow || null);
                }
            })
            .catch(() => setClientDetails(mainCompany || companyToShow || null))
            .finally(() => setTabLoading(false));
    }, [open, loading, activeTab, resolvedClientId, mainCompany, companyToShow]);
    const offerCompany = signator.company
        ? {
            company: signator.company,
            siret: signator.siret,
            address: signator.address,
            zipcode: signator.zipcode,
            city: signator.city,
            country: signator.country,
        }
        : packData?.company?._id
            ? {
                company: packData.company.name,
                siret: packData.company.siret,
                address: packData.company.address,
                zipcode: packData.company.zipCode,
                city: packData.company.city,
                country: packData.company.pays,
            }
            : companyToShow;

    const hasMainCompany = !!companyToShow;
    const hasOfferCompany = !!offerCompany;
    const showCompanySection = hasMainCompany || hasOfferCompany;

    const primaryContact = mainCompany || {
        lastName: record?.owner_lastName || source.lastName,
        firstName: record?.owner_firstName || source.firstName,
        email: record?.owner_email || source.email,
    };

    const offerRecipient = recipients[0] || {
        lastName: signator.lastname,
        firstName: signator.firstname,
        email: signator.email,
    };

    const trainees = (source.trainees || []).filter(Boolean).map((el) => ({
        gender: el.gender
            ? formatMessage({ id: `leads.form.gender.${el.gender}` })
            : '',
        lastName: el.lastName || '',
        firstName: el.firstName || '',
        birth: el.birth ? moment(el.birth).format('DD/MM/YYYY') : '',
        email: el.email || '',
        establishment: el.establishment || el.company || '',
    }));

    const headerDate = createdAt
        ? moment(createdAt).format('DD/MM/YYYY [à] HH:mm')
        : '';
    const shortId = id ? String(id).slice(-12) : '';

    const statusInternalOptions = LEAD_STATUSES.map((item) => ({
        value: item,
        label: formatMessage({ id: `leads.status.${item}` }).toUpperCase(),
    }));
    const statusExternalOptions = LEAD_EXTERNAL_STATUSES.map((item) => ({
        value: item,
        label: formatMessage({ id: `leads.status.${item}` }).toUpperCase(),
    }));
    const referenceOptions = [
        { value: '', label: '' },
        ...LEAD_REFERENCES.map((item) => ({
            value: item,
            label: formatMessage({ id: `leads.table.reference.${item}` }).toUpperCase(),
        })),
    ];
    const leadEvents = (record?.events || []).slice().sort(
        (a, b) => new Date(b.date) - new Date(a.date),
    );
    const historyRows = clientHistory.filter((item) => String(item.id) !== String(id));
    const clientInfo = clientDetails || mainCompany || companyToShow;

    const handleSave = () => {
        if (!id || saving) return;

        setSaving(true);
        API.updateLead(id, { status, statusExternal, reference, managementComment, offresOcPlus })
            .then((res) => {
                if (res.code === 200) {
                    showMessage({ message: 'Saved successfully', type: 'success' });
                    setLead((prev) => (prev ? {
                        ...prev,
                        status,
                        statusExternal,
                        reference,
                        managementComment,
                        offresOcPlus,
                        events: res.data?.events || prev.events,
                    } : prev));
                    if (onSaved) {
                        onSaved({ status, statusExternal, reference, managementComment, offresOcPlus });
                    }
                } else {
                    showMessage({ message: 'Failed to save', type: 'danger' });
                }
            })
            .catch(() => {
                showMessage({ message: 'Failed to save', type: 'danger' });
            })
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        if (!id) return;

        onDeleteById(id, 'deleteLead', () => {
            handleClose();
            if (onDeleted) {
                onDeleted();
            }
        });
    };

    const orderTitle = source.title || '';
    const orderComment = source.description || '';
    const stageCode = packData?.ref_id || packData?.refCode || source.refCode || '';
    const startDate = packData?.training_date
        ? moment(packData.training_date).format('DD/MM/YYYY')
        : source.orderDate
            ? moment(source.orderDate).format('DD/MM/YYYY')
            : '';
    const endDate = packData?.training_end_date
        ? moment(packData.training_end_date).format('DD/MM/YYYY')
        : '';
    const trainingCenter = packData?.training_center_name || '';
    const price = source.price
        ? `${Number.parseFloat(source.price) * (source.quantity || 1)} €`
        : '';
    const traineeCount = source.intra
        ? source.traineeCount
        : (source.trainees || []).filter(Boolean).length || source.quantity || '';

    return (
        <Modal
            open={open}
            onClose={handleClose}
            className={classes.modalRoot}
            style={{ zIndex: 100001 }}
            BackdropProps={{ className: classes.backdrop }}
            aria-labelledby="lead-modern-modal"
        >
            <div className={classes.paper}>
                <div className={classes.header}>
                    <Typography className={classes.headerTitle} id="lead-modern-modal">
                        {formatMessage(
                            { id: 'leads.detail.modernTitle' },
                            { id: shortId, date: headerDate },
                        )}
                    </Typography>
                    <div className={classes.headerActions}>
                        <Tooltip title={formatMessage({ id: 'save' })}>
                            <span>
                                <IconButton
                                    className={classes.headerBtn}
                                    onClick={handleSave}
                                    disabled={loading}
                                    aria-label="save"
                                >
                                    {saving ? (
                                        <CircularProgress size={18} style={{ color: '#fff' }} />
                                    ) : (
                                        <SaveIcon style={{ fontSize: 22 }} />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={formatMessage({ id: 'tooltip.delete' })}>
                            <span>
                                <IconButton
                                    className={classes.headerBtn}
                                    onClick={handleDelete}
                                    disabled={loading || saving}
                                    aria-label="delete"
                                >
                                    <DeleteIcon style={{ fontSize: 22 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton className={classes.closeBtn} onClick={handleClose} aria-label="close">
                            <CloseIcon style={{ fontSize: 25 }} />
                        </IconButton>
                    </div>
                </div>

                <div className={classes.body}>
                    <div className={classes.tabsRow}>
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                className={`${activeTab === tab ? classes.tabActive : classes.tabInactive} ${classes.tabButton}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {formatMessage({ id: TAB_I18N[tab] })}
                            </button>
                        ))}
                    </div>

                    {loading || (tabLoading && activeTab !== 'general') ? (
                        <div className={classes.loadingWrap}>
                            <CircularProgress style={{ color: '#030f40' }} />
                        </div>
                    ) : (
                        <>
                            {activeTab === 'general' && (
                        <>
                            <div className={classes.sectionBlockFirst}>
                                <Typography className={classes.sectionTitle}>
                                    {formatMessage({ id: 'leads.detail.leadManagement' })}
                                </Typography>
                                <div className={classes.leadManagementRow}>
                                    <div className={classes.leadManagementLeft}>
                                        <EditableSelectField
                                            classes={classes}
                                            label={fieldLabel(formatMessage, 'statusInternal')}
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            options={statusInternalOptions}
                                        />
                                        <EditableSelectField
                                            classes={classes}
                                            label={fieldLabel(formatMessage, 'statusExternal')}
                                            value={statusExternal}
                                            onChange={(e) => setStatusExternal(e.target.value)}
                                            options={statusExternalOptions}
                                        />
                                        <EditableSelectField
                                            classes={classes}
                                            label={fieldLabel(formatMessage, 'referent')}
                                            value={reference}
                                            onChange={(e) => setReference(e.target.value)}
                                            options={referenceOptions}
                                            displayEmpty
                                        />
                                        <div className={classes.leadManagementContratCadreCell}>
                                            <ReadOnlyField
                                                classes={classes}
                                                label={`${formatMessage({ id: 'leads.table.contratCadre' })} :`}
                                                value={resolvedContratCadre}
                                            />
                                        </div>
                                        <div className={classes.leadManagementOffresCell}>
                                            <EditableTextField
                                                classes={classes}
                                                label={`${formatMessage({ id: 'leads.table.offresOcPlus' })} :`}
                                                value={offresOcPlus}
                                                onChange={(e) => setOffresOcPlus(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className={classes.leadManagementCommentCol}>
                                        <EditableTextField
                                            classes={classes}
                                            label={fieldLabel(formatMessage, 'managementComment')}
                                            value={managementComment}
                                            onChange={(e) => setManagementComment(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {showCompanySection && (
                                <div className={classes.sectionBlock}>
                                    {hasMainCompany && hasOfferCompany ? (
                                        <div className={classes.dualColumnsRow}>
                                            <CompanyBlock
                                                classes={classes}
                                                formatMessage={formatMessage}
                                                company={companyToShow}
                                                title={formatMessage({ id: 'leads.detail.mainCompany' })}
                                            />
                                            <CompanyBlock
                                                classes={classes}
                                                formatMessage={formatMessage}
                                                company={offerCompany}
                                                title={formatMessage({ id: 'leads.detail.offerCompany' })}
                                            />
                                        </div>
                                    ) : (
                                        <CompanyBlock
                                            classes={classes}
                                            formatMessage={formatMessage}
                                            company={hasMainCompany ? companyToShow : offerCompany}
                                            title={formatMessage({
                                                id: hasMainCompany
                                                    ? 'leads.detail.mainCompany'
                                                    : 'leads.detail.offerCompany',
                                            })}
                                            fullWidth
                                        />
                                    )}
                                </div>
                            )}

                            <div className={classes.sectionBlock}>
                                <div className={classes.dualColumnsRow}>
                                    <ContactBlock
                                        classes={classes}
                                        formatMessage={formatMessage}
                                        contact={primaryContact}
                                        title={formatMessage({ id: 'leads.detail.mainContact' })}
                                    />
                                    <ContactBlock
                                        classes={classes}
                                        formatMessage={formatMessage}
                                        contact={offerRecipient}
                                        title={formatMessage({ id: 'leads.detail.offerRecipient' })}
                                    />
                                </div>
                            </div>

                            {record?.type !== LEAD_TYPES[1] && (
                                <div className={classes.sectionBlock}>
                                    <Typography className={classes.sectionTitle}>
                                        {formatMessage({ id: 'leads.detail.orderDetails' })}
                                    </Typography>
                                    <div className={classes.orderDetailsGrid}>
                                        <ReadOnlyField
                                            classes={classes}
                                            label={fieldLabel(formatMessage, 'trainingTitle')}
                                            value={orderTitle}
                                        />
                                        <div className={classes.orderCommentCell}>
                                            <ReadOnlyField
                                                classes={classes}
                                                label={fieldLabel(formatMessage, 'orderComment')}
                                                value={orderComment}
                                                multiline
                                                stretch
                                            />
                                        </div>
                                        <Grid container spacing={3} className={classes.orderDetailsLeftRow}>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'stageCode')}
                                                    value={stageCode}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'startDate')}
                                                    value={startDate}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'endDate')}
                                                    value={endDate}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={3} className={classes.orderDetailsLeftRow}>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'trainingCenter')}
                                                    value={trainingCenter}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'price')}
                                                    value={price}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'traineeCount')}
                                                    value={traineeCount}
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {trainees.length > 0 && (
                                        <div className={classes.traineesSection}>
                                            <div className={classes.traineesHeader}>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.civility' })}
                                                </span>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.lastnameHeader' })}
                                                </span>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.firstnameHeader' })}
                                                </span>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.birthHeader' })}
                                                </span>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.emailHeader' })}
                                                </span>
                                                <span className={classes.traineesHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.label.establishmentHeader' })}
                                                </span>
                                            </div>
                                            <div className={classes.traineesWrap}>
                                                {trainees.map((row, index) => (
                                                    <div
                                                        key={`${row.email}-${index}`}
                                                        className={`${classes.traineeRow} ${index % 2 === 1 ? classes.traineeRowAlt : ''}`}
                                                    >
                                                        <span className={classes.traineeCell}>{row.gender}</span>
                                                        <span className={classes.traineeCell}>{row.lastName}</span>
                                                        <span className={classes.traineeCell}>{row.firstName}</span>
                                                        <span className={classes.traineeCell}>{row.birth}</span>
                                                        <span className={classes.traineeCellEmail}>{row.email}</span>
                                                        <span className={classes.traineeCell}>{row.establishment}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                            )}

                            {activeTab === 'events' && (
                                <div className={classes.sectionBlockFirst}>
                                    {leadEvents.length === 0 ? (
                                        <Typography className={classes.emptyTabMessage}>
                                            {formatMessage({ id: 'leads.detail.emptyEvents' })}
                                        </Typography>
                                    ) : (
                                        <div className={classes.traineesSection}>
                                            <div className={classes.dataTableHeader}>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.events.dateTime' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.events.event' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.events.user' })}
                                                </span>
                                            </div>
                                            <div className={classes.dataTableWrap}>
                                                {leadEvents.map((event, index) => (
                                                    <div
                                                        key={`${event.type}-${event.date}-${index}`}
                                                        className={`${classes.dataTableRow} ${index % 2 === 1 ? classes.dataTableRowAlt : ''}`}
                                                    >
                                                        <span className={classes.dataTableCell}>
                                                            {event.date
                                                                ? moment(event.date).format('DD/MM/YYYY HH:mm')
                                                                : ''}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {formatLeadEventDescription(formatMessage, event)}
                                                            {event.type === 'comment' && event.toValue
                                                                ? ` : ${event.toValue}`
                                                                : ''}
                                                            {event.type === 'offres_oc_plus' && event.toValue
                                                                ? ` : ${event.toValue}`
                                                                : ''}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {event.userName || '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'history' && (
                                <div className={classes.sectionBlockFirst}>
                                    {historyRows.length === 0 ? (
                                        <Typography className={classes.emptyTabMessage}>
                                            {formatMessage({ id: 'leads.detail.emptyHistory' })}
                                        </Typography>
                                    ) : (
                                        <div className={classes.traineesSection}>
                                            <div className={classes.dataTableHeaderHistory}>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.history.reference' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.history.createdAt' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.history.type' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.history.status' })}
                                                </span>
                                                <span className={classes.dataTableHeaderCell}>
                                                    {formatMessage({ id: 'leads.detail.history.product' })}
                                                </span>
                                            </div>
                                            <div className={classes.dataTableWrap}>
                                                {historyRows.map((item, index) => (
                                                    <div
                                                        key={item.id}
                                                        className={`${classes.dataTableRowHistory} ${index % 2 === 1 ? classes.dataTableRowAlt : ''}`}
                                                    >
                                                        <span className={classes.dataTableCell}>
                                                            {String(item.id || '').slice(-12)}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {item.createdAt
                                                                ? moment(item.createdAt).format('DD/MM/YYYY HH:mm')
                                                                : ''}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {item.type
                                                                ? formatMessage({ id: `lead.type.${item.type}` })
                                                                : ''}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {formatStatusLabel(formatMessage, item.status)}
                                                        </span>
                                                        <span className={classes.dataTableCell}>
                                                            {getLeadProductLabel(formatMessage, item)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'client' && (
                                <div className={classes.sectionBlockFirst}>
                                    {!clientInfo ? (
                                        <Typography className={classes.emptyTabMessage}>
                                            {formatMessage({ id: 'leads.detail.emptyClient' })}
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={0}>
                                            <Grid item xs={12} className={classes.rowGap}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={6}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={fieldLabel(formatMessage, 'siret')}
                                                            value={clientInfo.siret}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={fieldLabel(formatMessage, 'siren')}
                                                            value={getSiren(clientInfo.siret)}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} className={classes.rowGap}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'company')}
                                                    value={clientInfo.company || clientInfo.name}
                                                />
                                            </Grid>
                                            <Grid item xs={12} className={classes.rowGap}>
                                                <ReadOnlyField
                                                    classes={classes}
                                                    label={fieldLabel(formatMessage, 'address')}
                                                    value={clientInfo.address}
                                                />
                                            </Grid>
                                            <Grid item xs={12} className={classes.rowGap}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={4}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={fieldLabel(formatMessage, 'zipcode')}
                                                            value={clientInfo.zipcode || clientInfo.zipCode}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={5}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={fieldLabel(formatMessage, 'city')}
                                                            value={clientInfo.city}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={fieldLabel(formatMessage, 'country')}
                                                            value={clientInfo.country || clientInfo.pays}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} className={classes.rowGap}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={6}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={formatMessage({ id: 'leads.detail.client.email' })}
                                                            value={clientInfo.email}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <ReadOnlyField
                                                            classes={classes}
                                                            label={formatMessage({ id: 'leads.detail.client.phone' })}
                                                            value={clientInfo.phone}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            {clientInfo.domains && (
                                                <Grid item xs={12} className={classes.rowGap}>
                                                    <ReadOnlyField
                                                        classes={classes}
                                                        label={formatMessage({ id: 'leads.detail.client.domains' })}
                                                        value={Array.isArray(clientInfo.domains)
                                                            ? clientInfo.domains.join(', ')
                                                            : clientInfo.domains}
                                                    />
                                                </Grid>
                                            )}
                                            {clientInfo.grantComptes && clientInfo.contractReference && (
                                                <Grid item xs={12}>
                                                    <ReadOnlyField
                                                        classes={classes}
                                                        label={formatMessage({ id: 'leads.detail.client.contractReference' })}
                                                        value={clientInfo.contractReference}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

FormDetailsModern.propTypes = {
    formatMessage: PropTypes.func.isRequired,
    data: PropTypes.object,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    companies: PropTypes.array,
    onSaved: PropTypes.func,
    onDeleted: PropTypes.func,
};

export default FormDetailsModern;
